import { Box, Center, Text } from "@chakra-ui/react";
import { SoundVisualizerRef } from "../../../types";
import { CountDownTimerBorder } from "./CountDownTimerBorder";
import { useAtom } from "jotai";
import { ReactElement, useEffect, useState } from "react";
import { CALIBRATION_MIN_LOG_POWER } from "../../../constants";
import { recordingPatternAtom, sound02Atom } from "../../../store";
import { decibelToVolume } from "../../../utils/calcVolume";
import { BarSoundVisualizerV2 } from "../../atoms/BarSoundVisualizerV2";
import { ColorChangeText } from "./ColorChangeText";
import recordWaitPanelImageUrl from "/src/assets/images/mci/record_wait_panel.png";
import recordSubtraction50ImageUrl from "/src/assets/images/mci/record_subtraction_50.png";
import recordSubtraction100ImageUrl from "/src/assets/images/mci/record_subtraction_100.png";

// 自動再生音声の文字毎表示秒
const spoken_per_character02 = 172;

/*
 * 読み上げ見本の表示
 */
function RecordSample(
  props: Readonly<{
    isWaiting: boolean;
  }>
): ReactElement {
  const [recordingPattern] = useAtom(recordingPatternAtom);

  // 録音パターンと何発話目かで、表示画像を変える
  let imgSrc;
  if (recordingPattern === 1) {
    imgSrc = recordSubtraction50ImageUrl;
  } else {
    imgSrc = recordSubtraction100ImageUrl;
  }
  return (
    <Box
      sx={{
        ".record_sample_box": {
          position: "relative",
          height: "440px",
          marginBottom: "20px",
        },
        ".record_sample_box img": {
          position: "absolute",
        },
        ".record_sample_img": {
          top: "4",
          height: "100%",
        },
        "@media (max-width: 599px)": {
          ".record_sample_box": {
            height: "310px",
            marginBottom: "80px",
          },
        },
        "@media (max-width: 375px) and (max-device-height: 667px)": {
          ".record_sample_box": {
            height: "260px",
            marginBottom: "60px",
          },
        },
        "@media (max-width: 375px) and (min-device-height: 812px)": {
          ".record_sample_box": {
            height: "300px",
            marginBottom: "80px",
          },
        },
      }}
    >
      <Box className="record_sample_box">
        <Center>
          <img className="record_sample_img" src={imgSrc} />
          {
            // 待機中は録音待機画像を出す
            props.isWaiting && (
              <img
                className="record_sample_img"
                src={recordWaitPanelImageUrl}
                alt=""
              />
            )
          }
        </Center>
      </Box>
    </Box>
  );
}

function PhraseToSpeechView(
  props: Readonly<{
    limitSeconds: number;
    isRecording: boolean;
    isExpired: boolean;
    phraseIndex: number;
    phraseText: string;
    onTextColoredEnd?: () => void;
  }>
): ReactElement {
  const [sound02] = useAtom(sound02Atom);

  const playSound = sound02;
  // let text;
  // let textClassname;
  // let interval;

  // 引き算
  // if (props.phraseIndex === 0) {
  const text = props.phraseText;
  const textClassname = "reading_text02";
  const interval = spoken_per_character02;
  // }

  // 音声再生
  useEffect(() => {
    playSound.play();
    return () => {
      playSound.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box
        sx={{
          ".guidance_wrapper": {
            width: "100%",
          },
          ".reading_text_wrapper": {
            paddingLeft: "2.5em",
            paddingTop: "0.5em", // phraseを下に下げる
          },
          ".reading_text01": {
            paddingTop: "0.2em",
            color: "#cccccc",
            fontSize: "30px",
            textIndent: "1.5em",
            width: "10.5em",
            margin: "0 auto",
          },
          ".reading_text01::before": {
            content: "''",
            float: "right",
            width: "3em",
            height: "1em",
          },
          ".reading_text02": {
            paddingTop: "0.2em",
            color: "#cccccc",
            fontSize: "30px",
            textIndent: "1.5em",
            width: "11em",
            margin: "0 auto",
          },
          ".reading_text02::before": {
            content: "''",
            float: "right",
            width: "3em",
            height: "1em",
          },
          "@media (max-width: 599px)": {
            ".reading_text_wrapper": {
              paddingTop: "0.3em", // モバイルでの調整
            },
            ".reading_text01": {
              fontSize: "22px",
              textIndent: "1.5em",
              width: "11em",
              margin: "0 auto",
            },
            ".reading_text01::before": {
              content: "''",
              float: "right",
              width: "3em",
              height: "1em",
            },
            ".reading_text02": {
              fontSize: "22px",
              textIndent: "1.5em",
              width: "11em",
              margin: "0 auto",
            },
            ".reading_text02::before": {
              content: "''",
              float: "right",
              width: "3em",
              height: "1em",
            },
          },
        }}
        borderRadius="20px"
        className="guidance_wrapper"
      >
        <CountDownTimerBorder
          isEnable={props.isRecording}
          isExpired={props.isExpired}
          setSeconds={props.limitSeconds}
          onCountDownComplete={() => {
            // カウントダウン完了時の処理
            // 録音終了は親コンポーネントで行うので不要
          }}
        >
          <Box sx={{ paddingY: "0.5em" }}>
            <Box className="reading_text_wrapper">
              <ColorChangeText
                text={text}
                color="#555555"
                classname={textClassname}
                interval={interval}
                onColorChangeTextComplete={props.onTextColoredEnd}
              />
            </Box>
          </Box>
        </CountDownTimerBorder>
      </Box>

      <Center sx={{ margin: "auto" }}>
        <Box>
          <Text
            sx={{
              color: "primary.theme_lv4",
            }}
          >
            ※「えーっと」「あー」などの
            <br />
            &emsp;「数字」以外の言葉は話さないで下さい
          </Text>
        </Box>
      </Center>
    </>
  );
}

type TateshinaRecordingProps = {
  currentPhraseIndex: number;
  phrase: string;
  limitSeconds: number;
  startRecording: () => void;
  isRecording: boolean;
  isExpired: boolean;
  visualizerRef: React.RefObject<SoundVisualizerRef>;
};

export function TateshinaCalcView(
  props: Readonly<TateshinaRecordingProps>
): ReactElement {
  // 読み上げ終了フラグ
  const [isPhraseRead, setIsPhraseRead] = useState(false);

  const underThreshold = decibelToVolume(CALIBRATION_MIN_LOG_POWER);

  return (
    <>
      <PhraseToSpeechView
        phraseIndex={props.currentPhraseIndex}
        phraseText={props.phrase}
        limitSeconds={props.limitSeconds}
        isRecording={props.isRecording}
        isExpired={props.isExpired}
        onTextColoredEnd={() => {
          // 文字色が全て変更されたら録音開始
          setIsPhraseRead(true);
          // 録音開始
          props.startRecording();
        }}
      />
      <BarSoundVisualizerV2
        ref={props.visualizerRef}
        upperThreshold={0.81}
        underThreshold={underThreshold}
        maxValue={1}
        height="40px"
        width="100%"
      />
      {/* <CountDownTimerBar
        isEnable={isRecording}
        setSeconds={limitSeconds}
        onCountDownComplete={() => {
          // 録音時間が満了したら録音停止
          setIsRecording(false);
          if (props.onStopRecording) props.onStopRecording();
        }}
      /> */}
      {/* 数字表示枠 */}
      <RecordSample isWaiting={!isPhraseRead} />
    </>
  );
}
